"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CircleCheck, ArrowRight } from "lucide-react";
const steps = [{
  title: "Auditoría Técnica",
  description: "Evaluamos tu infraestructura digital actual, competencia en tu sector, oportunidades de automatización y gaps tecnológicos críticos."
}, {
  title: "Estrategia Tecnológica",
  description: "Diseñamos arquitectura digital personalizada con soluciones AI, sistemas de automatización y roadmap de transformación digital escalable."
}, {
  title: "Prototipo Interactivo",
  description: "Desarrollamos demo funcional de tu plataforma digital con automatizaciones, integraciones y flujos de IA para validar concepto."
}, {
  title: "Optimización & Testing",
  description: "Refinamos flujos, optimizamos conversiones, validamos integraciones técnicas y preparamos sistemas para producción a escala."
}, {
  title: "Implementación & Escalado",
  description: "Desplegamos soluciones en producción, configuramos monitoreo avanzado, implementamos analytics y preparamos para crecimiento futuro."
}, {
  title: "Automatización Activa",
  description: "Sistemas operando 24/7, generando leads calificados automáticamente, capturando oportunidades sin intervención manual constante."
}];
export default function ElCaminoASuNuevoHogar() {
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -50% 0px",
      threshold: 0
    };
    const observerCallback: IntersectionObserverCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute("data-step-index"));
          if (!isNaN(index)) {
            setActiveStep(index);
          }
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    stepRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return <section data-section-id="16099" id="hoja-de-ruta-al-éxito" className="bg-background dark relative overflow-hidden py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-20 max-w-3xl">
          <h2 className="text-primary mb-6 text-4xl font-bold tracking-tight sm:text-5xl uppercase">
            Arquitectura de Transformación Digital
          </h2>
          <p className="text-muted-foreground text-lg sm:text-xl font-medium">
            Proceso sistemático de 6 fases para implementar soluciones AI, automatización y sistemas digitales que escalan tu operación exponencialmente.
          </p>
        </div>

        <div className="relative flex flex-col lg:flex-row lg:gap-20">
          {}
          <div className="hidden lg:block lg:w-1/3">
            <div className="sticky top-32 flex flex-col justify-between h-[calc(100vh-16rem)] min-h-[400px]">
              <div>
                <span className="text-secondary block text-lg font-bold uppercase tracking-widest">
                  Fase de Ejecución
                </span>
                <div className="text-primary mt-2 text-9xl font-extrabold leading-none tracking-tighter transition-all duration-500">
                  {String(activeStep + 1).padStart(2, "0")}
                </div>
                <div className="mt-8">
                  <h3 className="text-foreground text-3xl font-bold transition-all duration-300">
                    {steps[activeStep].title}
                  </h3>
                </div>
              </div>
              
              {}
              <div className="flex gap-2">
                {steps.map((_, i) => <div key={i} className={cn("h-1.5 rounded-sm transition-all duration-300", i === activeStep ? "bg-secondary w-8 shadow-[0_0_10px_rgba(234,179,8,0.5)]" : i < activeStep ? "bg-primary w-2" : "bg-muted w-2")} />)}
              </div>
            </div>
          </div>

          {}
          <div className="relative lg:w-2/3 lg:pl-10">
            {}
            <div className="bg-muted absolute left-4 top-4 bottom-0 w-px lg:left-0" />
            <div className="bg-secondary absolute left-4 top-4 w-px transition-all duration-500 lg:left-0" style={{
            height: `${activeStep / (steps.length - 1) * 100}%`
          }} />

            <div className="space-y-24 pb-24">
              {steps.map((step, i) => <div key={i} data-index={i} data-step-index={i} ref={el => {
              stepRefs.current[i] = el;
            }} className={cn("relative pl-16 transition-all duration-700 lg:pl-16", activeStep === i ? "opacity-100 translate-x-0" : "opacity-40 lg:opacity-20")}>
                  {}
                  <div className={cn("absolute left-[13px] top-2 flex h-3 w-3 -translate-x-1/2 items-center justify-center rounded-sm transition-all duration-500 lg:-left-[3px]", activeStep >= i ? "bg-secondary shadow-[0_0_15px_rgba(234,179,8,0.6)] scale-125" : "bg-muted")} />

                  {}
                  <span className="text-secondary mb-2 block text-sm font-bold uppercase tracking-widest lg:hidden">
                    Paso {String(i + 1).padStart(2, "0")}
                  </span>

                  <h3 className="text-foreground mb-4 text-2xl font-bold sm:text-3xl">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
                    {step.description}
                  </p>

                  {}
                  <div className={cn("mt-6 lg:hidden", activeStep === i ? "block" : "hidden")}>
                     <div className="h-1 w-12 bg-secondary rounded-sm" />
                  </div>
                </div>)}
            </div>

            {}
            <div className="relative pl-16 lg:pl-16">
               <div className="bg-secondary absolute left-[13px] top-8 h-4 w-4 -translate-x-1/2 rounded-sm lg:-left-[4px]" />
               <div className="bg-muted/30 backdrop-blur-md rounded-sm p-8 border border-border">
                  <h3 className="text-primary text-2xl font-bold mb-4 uppercase">¿Listo para tu Transformación Digital?</h3>
                  <p className="text-muted-foreground mb-6 font-medium">
                    Descubre cómo empresas modernas usan IA y automatización para dominar sus mercados. Consulta estratégica gratuita con expertos técnicos.
                  </p>
                  <Link to="#listo-para-crecer" onClick={e => {
                e.preventDefault();
                document.getElementById('listo-para-crecer')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 w-full sm:w-auto text-base font-bold h-14 px-10 rounded-sm shadow-lg flex items-center justify-center gap-2">
                    Solicita diagnóstico gratis <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
               </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}